import { useQueryClient } from "@tanstack/react-query";
import { cn } from "cn";
import { BadgeCheck } from "lucide-react";
import type { IssueDetail } from "../../types";
import { TimeAgo } from "../time-ago";
import { Card, CardHeader, CardTitle } from "../ui/card";
import { CancelIssueButton, RequestChangesDialog, SignOffButton } from "./sign-off-actions";
import { SignOffTesting, useStepsAllWorked } from "./test-steps";

/**
 * Business sign-off for an issue in business testing. An approver on one of
 * the issue's teams works through the test steps, then approves the merge,
 * sends their feedback to the Fixer, or cancels the issue. Everyone else sees why they cannot.
 */
export function SignOffCard({ issue, onChanged }: { issue: IssueDetail; onChanged: () => void }) {
  const queryClient = useQueryClient();
  const signOff = issue.signOff;
  const allWorked = useStepsAllWorked(issue);
  const refresh = () => {
    onChanged();
    void queryClient.invalidateQueries({ queryKey: ["task", issue.taskId] });
    void queryClient.invalidateQueries({ queryKey: ["issue-comments", issue.identifier] });
  };

  if (!signOff) return null;
  const hasSteps = allWorked !== null;
  // Until every step worked, testing is the main action and sign-off steps back.
  const testFirst = hasSteps && !allWorked;

  return (
    <Card className="gap-0 overflow-hidden py-0">
      <CardHeader className="px-4 py-3">
        <div className="flex items-start justify-between gap-3">
          <div className="space-y-1">
            <CardTitle className="flex items-center gap-2 text-sm">
              <BadgeCheck className="size-4 text-muted-foreground" />
              Business sign-off
            </CardTitle>
            <p className="text-xs text-muted-foreground">
              {signOff.approvedBy ? (
                <>
                  Signed off by {signOff.approvedBy}
                  {signOff.approvedAt && (
                    <>
                      {" "}
                      <TimeAgo at={signOff.approvedAt} />
                    </>
                  )}
                  . It merges once its checks pass, then ships with the next release.
                </>
              ) : signOff.canAct && allWorked ? (
                "Every test step worked. Sign off to merge it, or tell the Fixer what to change."
              ) : signOff.canAct && hasSteps ? (
                "Work through the test steps in the preview, then sign off to merge it or tell the Fixer what to change."
              ) : signOff.canAct ? (
                "Tried the change in the preview? Sign off to merge it, or tell the Fixer what to change."
              ) : (
                "Waiting for an approver to try the change and sign it off."
              )}
            </p>
          </div>
          {signOff.approvedBy && (
            <span className="flex shrink-0 items-center gap-1.5 rounded-full bg-emerald-500/10 px-2 py-1 text-[11px] font-medium text-emerald-700 dark:text-emerald-300">
              <span className="size-1.5 rounded-full bg-emerald-500" />
              Signed off
            </span>
          )}
        </div>
      </CardHeader>
      {!signOff.approvedBy && <SignOffTesting issue={issue} />}
      {!signOff.approvedBy && (
        <div className="space-y-2 border-t bg-muted/20 px-3 py-2.5">
          {signOff.canAct ? (
            <>
              <div className="flex gap-2">
                <SignOffButton
                  issue={issue}
                  size="sm"
                  variant={testFirst ? "outline" : "default"}
                  className={cn("flex-1", testFirst && "bg-background")}
                  onSigned={refresh}
                />
                <RequestChangesDialog issue={issue} className="flex-1 bg-background" onSent={refresh} />
              </div>
              <CancelIssueButton issue={issue} className="w-full" onCancelled={refresh} />
            </>
          ) : (
            <p className="text-xs text-muted-foreground">{signOff.reason}</p>
          )}
        </div>
      )}
    </Card>
  );
}
